import React, { useEffect, useRef, useState } from "react";
import CatalogImage from "../common/CatalogImage";
import { getCachedMenuProductImage, invalidateMenuProductImage, loadMenuProductImage } from "../../services/supabase/menuProductImages";
import "../../styles/MenuProductImage.css";

export default function MenuProductImage({ product, style, enabled = true }) {
  const ref = useRef(null);
  const directUrl = product.fotoUrl || product.imageUrl;
  const [visible, setVisible] = useState(false);
  const [result, setResult] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const key = JSON.stringify([product.imageScope, product.imageMenuId, product.id, product.imageVersion]);
  const cached = product.imageDeferred ? getCachedMenuProductImage(product) : undefined;
  const resolved = result?.key === key && result?.source === directUrl ? result : null;
  const url = resolved?.error ? null : directUrl || cached || resolved?.url;
  const loading = !url && !resolved && product.imageDeferred && cached === undefined;

  useEffect(() => { setAttempt(0); }, [key]);

  useEffect(() => {
    if (!ref.current || !enabled || !product.imageDeferred || directUrl) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); observer.disconnect(); }
    }, { rootMargin: "150px" });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [enabled, product.imageDeferred, directUrl, key]);

  useEffect(() => {
    if (!visible || !enabled || !product.imageDeferred || directUrl || cached !== undefined) return undefined;
    let active = true;
    loadMenuProductImage(product).then(
      (imageUrl) => { if (active) setResult({ key, source: directUrl, url: imageUrl }); },
      () => { if (active) setResult({ key, source: directUrl, error: true }); }
    );
    return () => { active = false; };
    // The key contains all image identity fields; other product edits do not reload photos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, visible, enabled, product.imageDeferred, directUrl, cached, attempt]);

  useEffect(() => {
    if (!resolved?.error || !enabled || attempt >= 2) return undefined;
    const timer = setTimeout(() => {
      if (navigator.onLine === false) return;
      setResult(null);
      setAttempt((value) => value + 1);
    }, attempt === 0 ? 1000 : 3000);
    return () => clearTimeout(timer);
  }, [resolved?.error, key, enabled, attempt]);

  useEffect(() => {
    const recover = () => {
      if (!resolved?.error) return;
      setResult(null);
      setAttempt((value) => value + 1);
    };
    window.addEventListener("online", recover);
    return () => window.removeEventListener("online", recover);
  }, [resolved?.error]);

  const retry = (event) => {
    event.stopPropagation();
    invalidateMenuProductImage(product.imageMenuId, product.id);
    setResult(null);
    setAttempt((value) => value + 1);
  };

  return <div ref={ref} className={`menu-product-photo${loading ? " is-loading" : ""}`}
    style={{ ...style, height: style?.height || style?.maxHeight || 180 }}
    aria-busy={loading}>
    <CatalogImage key={`${key}:${attempt}`} src={url} alt={product.nombre || "Vista previa"}
      loading="lazy" decoding="async" onError={() => {
        invalidateMenuProductImage(product.imageMenuId, product.id);
        setResult({ key, source: directUrl, error: true });
      }} />
    {!url && (loading || resolved?.error) ? <div className="menu-product-photo__status">
      {resolved?.error ? <>
        <span>La foto no termino de cargar</span>
        <button type="button" onClick={retry}>Volver a cargar foto</button>
      </> : <span>Preparando foto</span>}
    </div> : null}
  </div>;
}
